import { observable } from "@legendapp/state";
import { ObservablePersistLocalStorage } from "@legendapp/state/persist-plugins/local-storage";
import { synced } from "@legendapp/state/sync";

import { trpc } from "~/libs/trpc.lib";
import { activeOrganization$ } from "../organizations";
import type { Subscription } from "./subscription.types";

/**
 * The current org's subscription row regardless of status (trialing | active |
 * past_due | canceled | ...), or null. Sourced from `getCurrent` so the footer
 * trial badge can persist after the trial lapses and only disappears once the
 * org pays (status `active`). Read-only — mutations happen via the cardless
 * trial activation, Stripe Checkout, or the Customer Portal.
 */
export const subscription$ = observable<Subscription | null>(
  synced({
    initial: null,
    persist: {
      name: "subscription",
      plugin: ObservablePersistLocalStorage,
    },
    get: async () => {
      const orgId = activeOrganization$.id.get();
      if (!orgId) return null;

      const result = await trpc.subscriptions.getCurrent.query();
      subscriptionLoaded$.set(true);
      return result;
    },
  }),
);

/** True once the subscription fetch has settled (even if result is null). */
export const subscriptionLoaded$ = observable(false);

/** Re-fetch the subscription from the server (e.g. after activating the trial). */
export const refetchSubscription = async () => {
  const result = await trpc.subscriptions.getCurrent.query();
  subscription$.set(result);
  subscriptionLoaded$.set(true);
};
